import { US_STATE_TERRITORY_BOUNDS } from './usStateTerritoryBounds.js';

/**
 * @param {string | null | undefined} state
 * @returns {string | null}
 */
export function normalizeStateTerritoryCode(state) {
  if (state == null || state === '') return null;
  const code = String(state).trim().toUpperCase();
  return code.length >= 2 ? code.slice(0, 2) : null;
}

/**
 * @param {number} lat
 * @param {number} lng
 * @param {string} stateCode Two-letter STUSPS.
 * @returns {boolean}
 */
export function coordinateWithinStateTerritory(lat, lng, stateCode) {
  const code = normalizeStateTerritoryCode(stateCode);
  if (!code) return false;
  const bounds = US_STATE_TERRITORY_BOUNDS[code];
  if (!bounds) return false;

  const latN = Number(lat);
  const lngN = Number(lng);
  if (!Number.isFinite(latN) || !Number.isFinite(lngN)) return false;

  if (latN < bounds.minLat || latN > bounds.maxLat) return false;

  if (code === 'AK') {
    return lngN >= bounds.minLng || lngN <= bounds.maxLng;
  }

  return lngN >= bounds.minLng && lngN <= bounds.maxLng;
}

/**
 * Source-agnostic sanity check before an ED coordinate may go live.
 *
 * @param {number} lat
 * @param {number} lng
 * @param {string | null | undefined} state
 * @returns {{ ok: boolean, stateCode: string | null, stateLabel: string | null, message: string | null }}
 */
export function validateCoordinateForFacilityState(lat, lng, state) {
  const stateCode = normalizeStateTerritoryCode(state);
  if (!stateCode) {
    return {
      ok: false,
      stateCode: null,
      stateLabel: null,
      message: 'Facility state is missing or invalid.',
    };
  }

  const bounds = US_STATE_TERRITORY_BOUNDS[stateCode];
  if (!bounds) {
    return {
      ok: false,
      stateCode,
      stateLabel: null,
      message: `Unknown state or territory code: ${stateCode}.`,
    };
  }

  const latN = Number(lat);
  const lngN = Number(lng);
  if (!Number.isFinite(latN) || !Number.isFinite(lngN)) {
    return {
      ok: false,
      stateCode,
      stateLabel: bounds.label,
      message: 'Latitude and longitude must be valid numbers.',
    };
  }

  if (!coordinateWithinStateTerritory(latN, lngN, stateCode)) {
    return {
      ok: false,
      stateCode,
      stateLabel: bounds.label,
      message: `This point isn't in ${bounds.label} (${stateCode}).`,
    };
  }

  return {
    ok: true,
    stateCode,
    stateLabel: bounds.label,
    message: null,
  };
}
